import type { Font, TypographyConfig } from './themeComponents.types';

export const isFontFile = (url: string) => {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return /\.(woff2?|otf|ttf|eot)$/.test(pathname);
  } catch {
    return false;
  }
};

const loadFontStylesheet = (url: string) => {
  // Check if link already exists to avoid duplicates
  const existingLink = document.querySelector(`link[href="${url}"]`);
  if (existingLink) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

const loadCustomFont = async (font?: Font) => {
  if (!font?.fontFamily) {
    return;
  }

  if (!font.url) {
    return;
  }

  // Direct font files (.woff2, .woff, .otf, .ttf, .eot) can be loaded via FontFace API
  if (isFontFile(font.url)) {
    if (typeof FontFace === 'undefined') {
      console.error('FontFace API is not supported in this browser');
      return;
    }
    const fontFamily = font.fontFamily.toString();
    const fontFace = new FontFace(fontFamily, `url(${font.url})`);
    try {
      const loadedFont = await fontFace.load();
      document.fonts.add(loadedFont);
    } catch (error) {
      console.error(`Failed to load font "${fontFamily}":`, error);
    }
    return;
  }

  // Font service URLs (Google Fonts, Typekit, etc.) return CSS stylesheets
  loadFontStylesheet(font.url);
};

export const loadCustomFonts = async (
  customTypography: TypographyConfig,
  element: HTMLElement = document.body
) => {
  if (!customTypography) return;

  if (customTypography.fonts.branded) {
    await loadCustomFont(customTypography.fonts.branded);
    if (!customTypography.fonts.default) {
      element.style.fontFamily = 'var(--font-family-branded)';
    }
  }

  if (customTypography.fonts.default) {
    await loadCustomFont(customTypography.fonts.default);
    element.style.fontFamily = 'var(--font-family-default)';
  }
};
